import React, {
  ReactNode,
  useEffect,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import AddchartIcon from '@mui/icons-material/Addchart';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import IconButton from 'core/components/IconButton';
import Skeleton from 'core/components/Skeleton';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  VisitNoteDTO,
} from 'dtos';
import {
  useGetVisitNoteForPatient,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  isEmptyRichText,
  normalizeContent,
  removeFieldLabelsFromHtml,
} from 'views/EMR/Settings/helper';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  serviceCodesFormKeys,
} from 'views/EMR/VisitNoteForm';
import {
  filterCodes,
  filterCustomCodes,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import clsx from 'clsx';

type TextSectionConfig = {
  sectionType: 'text';
  title: string;
  field: 'additionalHPI' | 'physicalExam' | 'radiology' | 'additionalAssessment' | 'plan';
};

type CodeSectionConfig = {
  sectionType: 'code';
  title: string;
  field: 'assessmentCodes' | 'serviceCodes' | 'customServiceCodes';
  type: 'ICDCode' | 'CPTCode' | 'CustomCode';
  dropdownId: string;
  id: string;
  externalFilter: typeof filterCodes;
};

type SectionConfig = TextSectionConfig | CodeSectionConfig;

const SECTIONS: SectionConfig[] = [
  {
    sectionType: 'text',
    title: 'Subjective',
    field: 'additionalHPI',
  },
  {
    sectionType: 'text',
    title: 'Objective',
    field: 'physicalExam',
  },
  {
    sectionType: 'text',
    title: 'Review of Imaging',
    field: 'radiology',
  },
  {
    sectionType: 'code',
    title: 'Assessment (ICD-10)',
    field: 'assessmentCodes',
    type: 'ICDCode',
    dropdownId: 'icd-code-selection',
    id: 'assessmentCodes',
    externalFilter: filterCodes,
  },
  {
    sectionType: 'text',
    title: 'Additional Assessment',
    field: 'additionalAssessment',
  },
  {
    sectionType: 'text',
    title: 'Plan',
    field: 'plan',
  },
  {
    sectionType: 'code',
    title: 'Billing Codes',
    field: 'serviceCodes',
    type: 'CPTCode',
    dropdownId: 'cpt-code-selection',
    id: 'billingCodes',
    externalFilter: filterCodes,
  },
  {
    sectionType: 'code',
    title: 'Custom Codes',
    field: 'customServiceCodes',
    type: 'CustomCode',
    dropdownId: 'custom-code-selection',
    id: 'customServiceCodes',
    externalFilter: filterCustomCodes,
  },
];

type SectionProps = {
  title: string;
  children: ReactNode;
  hideApply?: boolean;
  isCurrentNoteFinalized?: boolean;
  onApply?: () => void;
};

function Section({
  title,
  children,
  hideApply = false,
  isCurrentNoteFinalized = false,
  onApply,
}: SectionProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-center gap-1">
        <h3 className="font-semibold text-sm">{title}</h3>
        {!hideApply && (
          <IconButton
            tooltip={isCurrentNoteFinalized ? 'Cannot apply to finalized note' : 'Apply to current visit note'}
            size="small"
            onClick={onApply ?? noop}
            aria-label="Apply"
            disabled={isCurrentNoteFinalized}
          >
            <AddchartIcon className={clsx('!w-4 !h-4 !text-blue-500', {
              '!text-gray-500': isCurrentNoteFinalized,
            })}
            />
          </IconButton>
        )}
      </div>
      <div className="px-2">
        {children}
      </div>
    </div>
  );
}

type SectionContentProps = { value: string };

function SectionContent({ value }: SectionContentProps) {
  const normalizedValue = useMemo(
    () => removeFieldLabelsFromHtml(normalizeContent(value, false)) ?? '',
    [value],
  );
  return (
    <div
      className="text-xs h-full overflow-y-auto ck-content"
      // oxlint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: normalizedValue }}
    />
  );
}

function PastNoteDetailsSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      <Skeleton variant="rectangular" className="!h-6 w-1/2 rounded" />
      <div className="px-2 w-full">
        <Skeleton variant="rectangular" className="!h-32 w-full rounded" />
      </div>
    </div>
  );
}

type VisitNoteContentProps = {
  visitNote?: VisitNoteDTO | null;
  isLoading?: boolean;
  hideApply?: boolean;
  isCurrentNoteFinalized?: boolean;
};

export function VisitNoteContent({
  visitNote,
  isLoading = false,
  hideApply = false,
  isCurrentNoteFinalized = false,
}: VisitNoteContentProps) {
  const { bill } = visitNote ?? {};

  return (isLoading || isNil(visitNote)) ? (
    <div className="flex flex-col gap-4 py-3 px-4">
      <PastNoteDetailsSkeleton />
      <PastNoteDetailsSkeleton />
    </div>
  ) : (
    <div className="flex flex-col gap-4 py-3 px-4 text-xs [word-break:break-word] border-y border-gray-200">
      {SECTIONS
        .filter((section) => (
          section.sectionType === 'text'
            ? notEmptyString(visitNote?.[section.field]) && !isEmptyRichText(visitNote?.[section.field] ?? '')
            : (bill?.[section.field] ?? []).length > 0
        ))
        .map((section) => (
          section.sectionType === 'text' ? (
            <Section
              key={section.field}
              title={section.title}
              hideApply={hideApply}
              isCurrentNoteFinalized={isCurrentNoteFinalized}
              onApply={() => visitNoteEventChannel.emit('onApplyTextField', section.field, visitNote?.[section.field] ?? '')}
            >
              <SectionContent value={visitNote?.[section.field] ?? ''} />
            </Section>
          ) : (
            <Section
              key={section.id}
              title={section.title}
              hideApply={hideApply}
              isCurrentNoteFinalized={isCurrentNoteFinalized}
              onApply={() => visitNoteEventChannel.emit('onApplyBillField', section.field, bill?.[section.field] ?? [])}
            >
              <ServicesLookup
                value={(bill?.[section.field] ?? []) as VisitNoteServiceCode[]}
                type={section.type}
                formKeys={serviceCodesFormKeys}
                dropdownId={section.dropdownId}
                id={section.id}
                allowDuplicate
                externalFilter={section.externalFilter}
                classes={{
                  root: 'w-full',
                  label: 'text-xs text-gray-500',
                }}
                readonly
                formEnabled={false}
                onChange={noop}
                normalizeOption={(option) => option as VisitNoteServiceCode}
                hideLabel
              />
            </Section>
          )
        ))}
    </div>
  );
}

type Props = {
  visitNoteId: number;
  patientId: number;
  hideApply?: boolean;
  isCurrentNoteFinalized?: boolean;
  onDataLoaded?: () => void;
};

export default function PastNoteDetails({
  visitNoteId,
  patientId,
  hideApply = false,
  isCurrentNoteFinalized = false,
  onDataLoaded,
}: Props) {
  const {
    data,
    isLoading,
  } = useGetVisitNoteForPatient(visitNoteId, patientId);

  useEffect(() => {
    if (!isLoading && !isNil(data)) {
      onDataLoaded?.();
    }
  }, [isLoading, data, onDataLoaded]);

  return (
    <VisitNoteContent
      visitNote={data}
      isLoading={isLoading}
      hideApply={hideApply}
      isCurrentNoteFinalized={isCurrentNoteFinalized}
    />
  );
}
